import { Component, OnInit, OnDestroy, ChangeDetectorRef, Input, HostBinding } from '@angular/core';
import { MessageService, SortEvent } from 'primeng/api';
import { debounceTime, distinctUntilChanged, Subject, Subscription } from 'rxjs';

import { TipoPadraoNota } from '../../../enums/TipoPadraoNota.enum';
import { TipoTransacao } from '../../../enums/TipoTransacao.enum';
import {
  TimbradoDTO,
  TimbradoFormDTO,
} from '../../../models/configuracoes/timbrao.model';
import { TimbradoService } from '../../../services/configuracoes/timbrado.service';
import { TranslateService } from '@ngx-translate/core';
import { TableColumn } from '../../../models/shared/table-column.model';

@Component({
  selector: 'app-timbrado',
  templateUrl: './timbrado.component.html',
  styleUrls: ['./timbrado.component.scss'],
  providers: [MessageService],
})
export class TimbradoComponent implements OnInit, OnDestroy {
  /**
   * Oculta a ação de excluir (usado quando o componente é embutido na área
   * do contador operando um cliente — lá o escopo é só listar/adicionar/editar).
   */
  @Input() ocultarExcluir = false;

  /** Oculta o cabeçalho próprio da tela (quando embutido, ex.: aba do contador). */
  @Input() ocultarCabecalho = false;

  /**
   * Marca o host com a classe `embutido` quando a tela é usada dentro do
   * Operar Cliente (sinalizado por `ocultarCabecalho`). Permite ajustes de
   * estilo scoped (ex.: altura do botão "Adicionar") sem vazar para a rota
   * standalone de Timbrado.
   */
  @HostBinding('class.embutido') get ehEmbutido(): boolean {
    return this.ocultarCabecalho;
  }

  // Controle de abas (0 = Cadastro, 1 = Lista)
  activeTabIndex: number = 1;

  deleteDialogVisible = false;
  deleteItemId: string | null = null;
  submitting = false;
  submitted = false;

  // Objetos de traducao (seguindo padrao nova-despesa)
  texts = {
    titulo: '',
    subtitulo: '',
    aba_cadastro: '',
    aba_lista: '',
    editando_timbrado: '',
    novo_timbrado: '',
    dados_do_timbrado: '',
    configuracao: '',
    lista_de_timbrados: '',
    nenhum_timbrado: '',
    ajuste_filtros: '',
    carregando: '',
    confirmacao_excluir: '',
    confirmacao: ''
  };

  labels = {
    timbrado: '',
    autorizacao: '',
    ponto_de_expedicao: '',
    numero_filial: '',
    proximo_numero: '',
    numero_maximo: '',
    tipo_de_transacao: '',
    tipo_nota: '',
    data_inicio: '',
    data_fim: '',
    id: '',
    acoes: '',
    itens_por_pagina: ''
  };

  placeholders = {
    timbrado: '',
    autorizacao: '',
    ponto_de_expedicao: '',
    numero_filial: '',
    proximo_numero: '',
    numero_maximo: '',
    tipo_de_transacao: '',
    tipo_nota: '',
    data_inicio: '',
    data_fim: '',
    pesquisar: ''
  };

  botoes = {
    salvar: '',
    atualizar: '',
    limpar: '',
    adicionar: '',
    confirmar: '',
    cancelar: ''
  };

  tooltips = {
    editar: '',
    excluir: ''
  };

  private langChangeSub!: Subscription;

  // Configuracao de colunas visiveis
  readonly STORAGE_KEY = 'timbrado-visible-columns';

  columns: TableColumn[] = [
    { key: 'id', labelKey: 'timbrado.labels.id', visible: true, fixed: true, defaultVisible: true },
    { key: 'timbrado', labelKey: 'timbrado.labels.timbrado', visible: true, fixed: true, defaultVisible: true },
    { key: 'tipoNota', labelKey: 'timbrado.labels.tipo_nota', visible: true, fixed: false, defaultVisible: true },
    { key: 'tipoDeTransacao', labelKey: 'timbrado.labels.tipo_de_transacao', visible: true, fixed: false, defaultVisible: true },
    { key: 'autorizacao', labelKey: 'timbrado.labels.autorizacao', visible: true, fixed: false, defaultVisible: true },
    { key: 'pontoDeExpedicao', labelKey: 'timbrado.labels.ponto_de_expedicao', visible: true, fixed: false, defaultVisible: false },
    { key: 'numeroFilial', labelKey: 'timbrado.labels.numero_filial', visible: true, fixed: false, defaultVisible: false },
    { key: 'proximoNumero', labelKey: 'timbrado.labels.proximo_numero', visible: true, fixed: false, defaultVisible: true },
    { key: 'numeroMaximo', labelKey: 'timbrado.labels.numero_maximo', visible: true, fixed: false, defaultVisible: true },
    { key: 'acoes', labelKey: 'timbrado.labels.acoes', visible: true, fixed: true, defaultVisible: true },
  ];

  products: TimbradoDTO[] = [];
  private search$ = new Subject<string>();
  first = 0;
  rows = 10;
  private _lastRows: number = 10;
  totalRecords = 0;
  loading = true;

  // Estado da ordenação server-side
  currentSortField: string = 'id';
  currentSortOrder: 'asc' | 'desc' = 'asc';

  pageSizeOptions = [
    { label: 5, value: 5 },
    { label: 10, value: 10 },
    { label: 20, value: 20 },
    { label: 120, value: 120 },
  ];

  form: TimbradoFormDTO = this.createEmptyForm();
  tipoNota: { label: string; value: TipoPadraoNota }[] = [];
  tipoDeTransacao: { label: string; value: TipoTransacao }[] = [];
  filterName = '';

  constructor(
    private messageService: MessageService,
    private timbradoService: TimbradoService,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef,
  ) { }

  ngOnInit() {
    this.loadColumnPreferences();
    this.buildDropdownOptions();
    this.findTimbrado();
    this.setupSearchDebounce();
    this.loadTranslations();

    // Atualiza traducoes e dropdowns quando idioma muda
    this.langChangeSub = this.translate.onLangChange.subscribe(() => {
      this.loadTranslations();
      this.buildDropdownOptions();
    });
  }

  ngOnDestroy(): void {
    this.search$.complete();
    if (this.langChangeSub) {
      this.langChangeSub.unsubscribe();
    }
  }

  onTabChange(event: { index: number }): void {
    if (event.index === 1) {
      this.texts.titulo = this.translate.instant('timbrado.texts.titulo');
      this.texts.subtitulo = this.translate.instant('timbrado.texts.subtitulo_lista');
    } else {
      const isEdicao = !!this.form.id;
      this.texts.titulo = this.translate.instant(
        isEdicao ? 'timbrado.texts.titulo_editar' : 'timbrado.texts.titulo'
      );
      this.texts.subtitulo = this.translate.instant(
        isEdicao ? 'timbrado.texts.subtitulo_editar' : 'timbrado.texts.subtitulo_novo'
      );
    }
  }

  private loadTranslations(): void {
    const isListaAtiva = this.activeTabIndex === 1;
    const isEdicao = !!this.form.id;
    this.texts = {
      titulo: this.translate.instant(
        isListaAtiva ? 'timbrado.texts.titulo' : isEdicao ? 'timbrado.texts.titulo_editar' : 'timbrado.texts.titulo'
      ),
      subtitulo: this.translate.instant(
        isListaAtiva ? 'timbrado.texts.subtitulo_lista' : isEdicao ? 'timbrado.texts.subtitulo_editar' : 'timbrado.texts.subtitulo_novo'
      ),
      aba_cadastro: this.translate.instant('timbrado.texts.aba_cadastro'),
      aba_lista: this.translate.instant('timbrado.texts.aba_lista'),
      editando_timbrado: this.translate.instant('timbrado.texts.titulo_editar'),
      novo_timbrado: this.translate.instant('timbrado.texts.titulo'),
      dados_do_timbrado: this.translate.instant('timbrado.texts.dados_do_timbrado'),
      configuracao: this.translate.instant('timbrado.texts.configuracao'),
      lista_de_timbrados: this.translate.instant('timbrado.texts.lista_de_timbrados'),
      nenhum_timbrado: this.translate.instant('timbrado.texts.nenhum_timbrado'),
      ajuste_filtros: this.translate.instant('timbrado.texts.ajuste_filtros'),
      carregando: this.translate.instant('timbrado.texts.carregando'),
      confirmacao_excluir: this.translate.instant('timbrado.texts.confirmacao_excluir'),
      confirmacao: this.translate.instant('common.confirmacao')
    };

    this.labels = {
      timbrado: this.translate.instant('timbrado.labels.timbrado'),
      autorizacao: this.translate.instant('timbrado.labels.autorizacao'),
      ponto_de_expedicao: this.translate.instant('timbrado.labels.ponto_de_expedicao'),
      numero_filial: this.translate.instant('timbrado.labels.numero_filial'),
      proximo_numero: this.translate.instant('timbrado.labels.proximo_numero'),
      numero_maximo: this.translate.instant('timbrado.labels.numero_maximo'),
      tipo_de_transacao: this.translate.instant('timbrado.labels.tipo_de_transacao'),
      tipo_nota: this.translate.instant('timbrado.labels.tipo_nota'),
      data_inicio: this.translate.instant('timbrado.labels.data_inicio'),
      data_fim: this.translate.instant('timbrado.labels.data_fim'),
      id: this.translate.instant('timbrado.labels.id'),
      acoes: this.translate.instant('timbrado.labels.acoes'),
      itens_por_pagina: this.translate.instant('timbrado.labels.itens_por_pagina')
    };

    this.placeholders = {
      timbrado: this.translate.instant('timbrado.placeholders.timbrado'),
      autorizacao: this.translate.instant('timbrado.placeholders.autorizacao'),
      ponto_de_expedicao: this.translate.instant('timbrado.placeholders.ponto_de_expedicao'),
      numero_filial: this.translate.instant('timbrado.placeholders.numero_filial'),
      proximo_numero: this.translate.instant('timbrado.placeholders.proximo_numero'),
      numero_maximo: this.translate.instant('timbrado.placeholders.numero_maximo'),
      tipo_de_transacao: this.translate.instant('timbrado.placeholders.tipo_de_transacao'),
      tipo_nota: this.translate.instant('timbrado.placeholders.tipo_nota'),
      data_inicio: this.translate.instant('timbrado.placeholders.data_inicio'),
      data_fim: this.translate.instant('timbrado.placeholders.data_fim'),
      pesquisar: this.translate.instant('timbrado.placeholders.pesquisar')
    };

    this.botoes = {
      salvar: this.translate.instant('timbrado.botoes.salvar'),
      atualizar: this.translate.instant('timbrado.botoes.atualizar'),
      limpar: this.translate.instant('timbrado.botoes.limpar'),
      adicionar: this.translate.instant('timbrado.botoes.adicionar'),
      confirmar: this.translate.instant('timbrado.botoes.confirmar'),
      cancelar: this.translate.instant('timbrado.botoes.cancelar')
    };

    this.tooltips = {
      editar: this.translate.instant('timbrado.tooltips.editar'),
      excluir: this.translate.instant('timbrado.tooltips.excluir')
    };
  }

  /** Constroi opcoes dos dropdowns com labels internacionalizados */
  private buildDropdownOptions(): void {
    this.tipoNota = Object.values(TipoPadraoNota).map(tipo => ({
      label: this.translate.instant(`enums.tipo_padrao_nota.${tipo}`),
      value: tipo,
    }));

    const tiposPermitidos = [TipoTransacao.VENDA, TipoTransacao.DEVOLUCAO];
    this.tipoDeTransacao = tiposPermitidos.map(tipo => ({
      label: this.translate.instant(`enums.tipo_transacao.${tipo}`),
      value: tipo,
    }));
  }

  findTimbrado(
    page: number = 0,
    size: number = this.rows,
    sort: string = `${this.currentSortField},${this.currentSortOrder}`,
    search: string | undefined = this.filterName?.trim() || undefined
  ) {
    this.loading = true;

    // Quando há termo de busca, usa o endpoint de busca geral (server-side)
    // GET /timbrado/buscar-geral?termoBusca=<termo>&page=&size=&sort=
    // Quando vazio, usa a listagem normal GET /timbrado com paginação
    const endpoint = search ? '/timbrado/buscar-geral' : '/timbrado';
    const params: any = search
      ? { termoBusca: search, page, size, sort }
      : { page, size, sort };

    this.timbradoService
      .get(endpoint, params)
      .subscribe({
        next: (response) => {
          this.products = response.content || [];
          this.totalRecords = response.totalElements || this.products.length;
          this.first = page * size;
          this.loading = false;
        },
        error: (err) => {
          // O endpoint /timbrado/buscar-geral retorna 404 quando não há resultados.
          // Nesse caso, exibimos lista vazia em vez de mensagem de erro.
          if (search && err?.status === 404) {
            this.products = [];
            this.totalRecords = 0;
            this.first = 0;
          } else {
            this.products = [];
            this.totalRecords = 0;
          }
          this.loading = false;
        }
      });
  }

  onPageChange(event: any) {
    const page = event.page;
    const size = event.rows;
    this.findTimbrado(page, size);
  }

  onRowsPerPageChange() {
    if (this.rows === this._lastRows) return;
    this._lastRows = this.rows;
    this.first = 0;
    this.findTimbrado(0, this.rows);
  }

  /** Ordenacao server-side com guard de re-emissao para evitar loop infinito */
  customSort(event: SortEvent): void {
    if (!event.field) return;
    const field = event.field;
    const novoSort = (event.order ?? 1) === 1 ? 'asc' : 'desc';

    // Guarda contra re-emissao: ignora se campo e direcao nao mudaram
    if (field === this.currentSortField && novoSort === this.currentSortOrder) {
      return;
    }

    this.currentSortField = field;
    this.currentSortOrder = novoSort;
    this.first = 0;
    this.findTimbrado(0, this.rows);
  }

  submit() {
    if (this.submitting) return;

    this.submitted = true;

    if (!this.validarFormularioTimbrado(this.form)) return;

    const body: TimbradoDTO = {
      timbrado: this.form.timbrado,
      tipoNota: this.form.tipoNota?.value as TipoPadraoNota,
      tipoDeTransacao: this.form.tipoDeTransacao?.value as TipoTransacao,
      autorizacao: this.form.autorizacao,
      dataInicio: this.formatDateToString(this.form.dataInicio),
      dataFim: this.ehTipoNotaEletronica ? null : this.formatDateToString(this.form.dataFim),
      pontoDeExpedicao: Number(this.form.pontoDeExpedicao),
      numeroFilial: Number(this.form.numeroFilial),
      proximoNumero: Number(this.form.proximoNumero),
      numeroMaximo: this.ehTipoNotaEletronica ? null : Number(this.form.numeroMaximo),
    };

    this.submitting = true;
    this.timbradoService.post('/timbrado', body).subscribe({
      next: () => {
        this.submitting = false;
        this.submitted = false;
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('common.sucesso'),
          detail: this.translate.instant('Timbrado_salvo_com_suceso'),
        });
        this.limparForm();
        this.findTimbrado();
        this.activeTabIndex = 1;
      },
      error: () => {
        this.submitting = false;
      },
    });
  }

  deletarTimbrado(id: string) {
    // Modo embutido (aba do Operar Cliente, sob contexto do contador): exclusao fora de
    // escopo — e o que `ocultarExcluir` ja sinaliza no template. Bloqueio defensivo: a
    // allowlist do contexto nao tem DELETE nenhum, entao a chamada acabaria excluindo na
    // empresa do PROPRIO contador (o interceptor nao injeta o header em DELETE).
    if (this.ocultarExcluir) {
      return;
    }
    this.deleteItemId = id;
    this.deleteDialogVisible = true;
  }

  confirmarExclusao() {
    if (!this.deleteItemId) return;
    this.timbradoService.delete(`/timbrado/${this.deleteItemId}`).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('common.sucesso'),
          detail: this.translate.instant('Timbrado_deletado_com_sucesso'),
        });
        this.findTimbrado();
        this.deleteDialogVisible = false;
        this.deleteItemId = null;
      },
      error: () => {
        this.deleteDialogVisible = false;
        this.deleteItemId = null;
      },
    });
  }

  editarTimbrado(event: TimbradoDTO) {
    // Extrai as chaves de enum vindas do backend
    const tipoNotaEnum = this.asEnumString(event.tipoNota);
    const tipoTransEnum = this.asEnumString(event.tipoDeTransacao);

    const tipoNota = this.tipoNota.find(opt =>
      String(opt.value) === tipoNotaEnum ||
      String(opt.label) === tipoNotaEnum
    );

    const tipoDeTransacao = this.tipoDeTransacao.find(opt =>
      String(opt.value) === tipoTransEnum
      || String(opt.label) === tipoTransEnum
    );

    this.form = {
      id: event.id,
      timbrado: event.timbrado ?? '',
      tipoNota: tipoNota,
      tipoDeTransacao: tipoDeTransacao,
      autorizacao: event.autorizacao ?? '',
      dataInicio: this.parseStringToDate(event.dataInicio),
      dataFim: this.parseStringToDate(event.dataFim),
      pontoDeExpedicao: event.pontoDeExpedicao ?? null,
      numeroFilial: event.numeroFilial ?? null,
      proximoNumero: event.proximoNumero ?? null,
      numeroMaximo: event.numeroMaximo ?? null,
    };

    this.submitted = false;
    this.activeTabIndex = 0;
    this.texts.titulo = this.translate.instant('timbrado.texts.titulo_editar');
    this.texts.subtitulo = this.translate.instant('timbrado.texts.subtitulo_editar');
    this.cdr.detectChanges();
  }


  createEmptyForm(): TimbradoFormDTO {
    return {
      timbrado: '',
      tipoNota: undefined,
      tipoDeTransacao: undefined,
      autorizacao: '',
      dataInicio: null,
      dataFim: null,
      pontoDeExpedicao: null,
      numeroFilial: null,
      proximoNumero: null,
      numeroMaximo: null,
    };
  }

  private asEnumString(v: any): string {
    if (v && typeof v === 'object' && 'enum' in v) return String(v.enum);
    return String(v ?? '');
  }

  /** Converte Date para string no formato dd/MM/yyyy */
  private formatDateToString(date: Date | null): string | undefined {
    if (!date) return undefined;
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }

  /** Converte string no formato dd/MM/yyyy para Date */
  private parseStringToDate(dateStr: string | null | undefined): Date | null {
    if (!dateStr) return null;
    const parts = dateStr.split('/');
    if (parts.length !== 3) return null;
    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1;
    const year = parseInt(parts[2], 10);
    return new Date(year, month, day);
  }

  novoTimbrado(): void {
    this.limparForm();
    this.activeTabIndex = 0;
  }

  limparForm() {
    this.form = this.createEmptyForm();
    this.submitted = false;
  }

  salvarEdicaoTimbrado() {
    if (this.submitting) return;

    this.submitted = true;

    if (!this.validarFormularioTimbrado(this.form)) return;

    const body: TimbradoDTO = {
      id: (this.form as any).id,
      timbrado: this.form.timbrado,
      tipoNota: this.form.tipoNota?.value as TipoPadraoNota,
      tipoDeTransacao: this.form.tipoDeTransacao?.value as TipoTransacao,
      autorizacao: this.form.autorizacao,
      dataInicio: this.formatDateToString(this.form.dataInicio),
      dataFim: this.ehTipoNotaEletronica ? null : this.formatDateToString(this.form.dataFim),
      pontoDeExpedicao: Number(this.form.pontoDeExpedicao),
      numeroFilial: Number(this.form.numeroFilial),
      proximoNumero: Number(this.form.proximoNumero),
      numeroMaximo: this.ehTipoNotaEletronica ? null : Number(this.form.numeroMaximo),
    };

    this.submitting = true;
    this.timbradoService.put(`/timbrado/${body.id}`, body).subscribe({
      next: () => {
        this.submitting = false;
        this.submitted = false;
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('common.sucesso'),
          detail: this.translate.instant('Timbrado_editado_com_sucesso'),
        });
        this.limparForm();
        this.findTimbrado();
        this.activeTabIndex = 1;
      },
      error: () => {
        this.submitting = false;
      },
    });
  }

  /** Valida o formulario de timbrado. Dispara mensagem de erro e retorna false se invalido. */
  private validarFormularioTimbrado(form: TimbradoFormDTO): boolean {
    if (!form.timbrado || form.timbrado.trim() === '') {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('common.erro'),
        detail: this.translate.instant('O_campo_Timbrado_e_obrigatorio'),
      });
      return false;
    }

    // O backend so aceita digitos no timbrado. Avisa aqui para o usuario nao
    // descobrir o erro so depois de enviar o formulario inteiro.
    if (!this.contemSomenteNumeros(form.timbrado)) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('common.erro'),
        detail: this.translate.instant('timbrado.mensagens.timbrado_somente_numeros'),
      });
      return false;
    }

    if (!form.tipoDeTransacao) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('common.erro'),
        detail: this.translate.instant('O_campo_Tipo_de_Transacao_e_obrigatorio'),
      });
      return false;
    }

    if (!form.tipoNota) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('common.erro'),
        detail: this.translate.instant('Selecionar_o_campo_Tipo_Nota_e_obrigatorio'),
      });
      return false;
    }

    // Autorizacao obrigatoria quando tipoNota for AUTO_IMPRESSOR.
    const tipoNotaValor = form.tipoNota?.value;
    if (
      tipoNotaValor === 'AUTO_IMPRESSOR' &&
      (!form.autorizacao || form.autorizacao.trim() === '')
    ) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('common.erro'),
        detail: this.translate.instant('Autorizacao_obrigatoria_para_Auto_Impressor'),
      });
      return false;
    }

    // A autorizacao segue a mesma regra do timbrado no backend: so digitos.
    if (form.autorizacao && !this.contemSomenteNumeros(form.autorizacao)) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('common.erro'),
        detail: this.translate.instant('timbrado.mensagens.autorizacao_somente_numeros'),
      });
      return false;
    }

    if (!form.dataInicio) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('common.erro'),
        detail: this.translate.instant('O_campo_Data_Inicio_e_obrigatorio'),
      });
      return false;
    }

    // Timbrado ELETRÔNICO não tem data de vencimento (Fecha Fin).
    if (!this.ehTipoNotaEletronica && !form.dataFim) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('common.erro'),
        detail: this.translate.instant('O_campo_Data_Fim_e_obrigatorio'),
      });
      return false;
    }

    const numeroFilialValor = Number(form.numeroFilial);
    if (
      form.numeroFilial === null ||
      form.numeroFilial === undefined ||
      isNaN(numeroFilialValor) ||
      numeroFilialValor < 1 ||
      numeroFilialValor > 999
    ) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('common.erro'),
        detail: this.translate.instant('Numero_Filial_deve_estar_entre_1_e_999'),
      });
      return false;
    }

    const pontoDeExpedicaoValor = Number(form.pontoDeExpedicao);
    if (
      form.pontoDeExpedicao === null ||
      form.pontoDeExpedicao === undefined ||
      isNaN(pontoDeExpedicaoValor) ||
      pontoDeExpedicaoValor < 1 ||
      pontoDeExpedicaoValor > 999
    ) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('common.erro'),
        detail: this.translate.instant('Ponto_de_Expedicao_deve_estar_entre_1_e_999'),
      });
      return false;
    }

    if (form.proximoNumero === null || form.proximoNumero === undefined) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('common.erro'),
        detail: this.translate.instant('O_campo_Proximo_Numero_e_obrigatorio'),
      });
      return false;
    }

    // Timbrado ELETRÔNICO não tem número máximo.
    if (!this.ehTipoNotaEletronica && (form.numeroMaximo === null || form.numeroMaximo === undefined)) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('common.erro'),
        detail: this.translate.instant('O_campo_Numero_Maximo_e_obrigatorio'),
      });
      return false;
    }

    return true;
  }

  /** Retorna true se o texto contiver apenas digitos (0-9). */
  private contemSomenteNumeros(valor: string): boolean {
    return /^\d+$/.test(valor.trim());
  }

  /** true quando o Tipo de Nota selecionado é ELETRÔNICO (sem data fim nem número máximo). */
  get ehTipoNotaEletronica(): boolean {
    return this.form.tipoNota?.value === TipoPadraoNota.ELETRONICA;
  }

  private setupSearchDebounce() {
    this.search$.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(() => {
      this.first = 0;
      this.findTimbrado(0, this.rows);
    });
  }

  onSearchChange(value: string) {
    this.filterName = value || '';
    this.search$.next(this.filterName);
  }

  isColumnVisible(key: string): boolean {
    const col = this.columns.find(c => c.key === key);
    return col ? col.visible : true;
  }

  get visibleColumnCount(): number {
    return this.columns.filter(c => c.visible).length;
  }

  onColumnsChange(columns: TableColumn[]): void {
    this.columns = [...columns];
  }

  private loadColumnPreferences(): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const visibleKeys = JSON.parse(stored) as string[];
        this.columns.forEach(col => {
          if (!col.fixed) {
            col.visible = visibleKeys.includes(col.key);
          }
        });
      }
    } catch (e) {
      console.warn('Erro ao carregar preferências de colunas:', e);
    }
  }


}
